import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  createContentItem,
  deleteContentItem,
  deleteContentMedia,
  getAdminPageContent,
  updateContentActive,
  updateContentOrder,
  updateContentValue,
  uploadContentMedia,
} from "../../../api/contentAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { useTranslation } from "react-i18next";
import EditableContentRow from "./EditableContentRow";
import {
  flattenContent,
  formatFieldTitle,
  getSectionKey,
  languages,
  normalizeLanguage,
  resolveMediaUrl,
  toSectionTitle,
} from "./contentManagerUtils";

const ContentManager = ({ t, selectedPage = "home" }) => {
  const { notify } = useNotification();
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(() => normalizeLanguage(i18n.language));
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [createModalSection, setCreateModalSection] = useState("");
  const [createForm, setCreateForm] = useState({
    label: "",
    key: "",
    type: "text",
    value: "",
    isActive: true,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [cardDrafts, setCardDrafts] = useState({});
  const [busyCardSections, setBusyCardSections] = useState({});
  const [cardUploadFiles, setCardUploadFiles] = useState({});

  const cardKeyRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getAdminPageContent({ page: selectedPage, language });
      setRawData(data);
    } catch (error) {
      notify({ type: "error", title: "Failed to load content", message: error?.message || "" });
      setRawData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [language, selectedPage]);

  useEffect(() => {
    const selected = normalizeLanguage(i18n.language);
    setLanguage((prev) => (prev === selected ? prev : selected));
  }, [i18n.language]);

  const normalizedItems = useMemo(() => flattenContent(rawData), [rawData]);
  const groupedItems = useMemo(() => {
    const groups = new Map();
    let sectionPosition = 0;
    normalizedItems.forEach((item) => {
      const sectionKey = getSectionKey({
        path: item.path,
        sectionKey: item.sectionKey,
        key: item.key,
        backendSection: item.backendSection,
      });
      if (!groups.has(sectionKey)) {
        groups.set(sectionKey, { items: [], firstSeenOrder: sectionPosition++ });
      }
      groups.get(sectionKey).items.push(item);
    });

    return Array.from(groups.entries())
      .map(([sectionKey, payload]) => {
        const sectionItems = payload.items || [];
        const minId = sectionItems.reduce((min, it) => {
          const id = Number(it.id);
          if (!Number.isFinite(id)) return min;
          return Math.min(min, id);
        }, Number.POSITIVE_INFINITY);

        return {
          sectionKey,
          sectionTitle: toSectionTitle(sectionKey),
          items: [...sectionItems].sort((a, b) => (a.order || 0) - (b.order || 0)),
          firstSeenOrder: payload.firstSeenOrder,
          minId,
        };
      })
      .sort((a, b) => {
        const aHasId = Number.isFinite(a.minId);
        const bHasId = Number.isFinite(b.minId);
        if (aHasId && bHasId) return a.minId - b.minId;
        if (aHasId) return -1;
        if (bHasId) return 1;
        return a.firstSeenOrder - b.firstSeenOrder;
      })
      .map(({ firstSeenOrder, minId, ...group }) => group);
  }, [normalizedItems]);
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    if (!groupedItems.length) return;
    setOpenSections((prev) => {
      const next = { ...prev };
      groupedItems.forEach((group, idx) => {
        if (typeof next[group.sectionKey] === "undefined") {
          next[group.sectionKey] = false;
        }
      });
      return next;
    });
  }, [groupedItems]);

  const requireId = (item) => {
    if (!item?.id) {
      notify({ type: "error", title: "Missing item id", message: "This item cannot be edited from admin endpoint." });
      return false;
    }
    return true;
  };

  const actionWrap = async (fn, successTitle, options = { reload: true }) => {
    try {
      await fn();
      notify({ type: "success", title: successTitle });
      if (options.reload) {
        await loadData();
      }
    } catch (error) {
      notify({
        type: "error",
        title: "Action failed",
        message: error?.response?.data?.message || error?.message || "Try again.",
      });
    }
  };

  const getCardFieldMeta = (items) => {
    const cardItems = items.filter((item) => cardKeyRegex.test(String(item.key || "")));
    if (!cardItems.length) return null;

    const cardsMap = new Map();
    const suffixOrder = [];
    const suffixSeen = new Set();

    cardItems.forEach((item) => {
      const match = String(item.key || "").match(cardKeyRegex);
      if (!match) return;
      const index = Number(match[1]);
      const suffix = String(match[2] || "");
      const normalizedSuffix = suffix.toLowerCase();
      if (!cardsMap.has(index)) cardsMap.set(index, []);
      cardsMap.get(index).push({ suffix, normalizedSuffix, item });
      if (!suffixSeen.has(normalizedSuffix)) {
        suffixSeen.add(normalizedSuffix);
        suffixOrder.push(normalizedSuffix);
      }
    });

    const cards = Array.from(cardsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, fields]) => ({
        index,
        fields: [...fields].sort(
          (a, b) =>
            suffixOrder.indexOf(a.normalizedSuffix) - suffixOrder.indexOf(b.normalizedSuffix)
        ),
      }));

    return { cards, suffixOrder, cardItems };
  };

  const toKeyFromLabel = (label) =>
    String(label || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const openCreateModal = (sectionKey) => {
    setCreateModalSection(sectionKey);
    setCreateForm({
      label: "",
      key: "",
      type: "text",
      value: "",
      isActive: true,
    });
  };

  const closeCreateModal = () => {
    if (isCreating) return;
    setCreateModalSection("");
  };

  const handleCreateSubmit = async () => {
    if (!createModalSection) return;
    const label = createForm.label.trim();
    const key = createForm.key.trim() || toKeyFromLabel(label);
    if (!label) {
      notify({ type: "error", title: "Label is required" });
      return;
    }
    if (!key) {
      notify({ type: "error", title: "Key is required", message: "Use a valid label or enter a key manually." });
      return;
    }

    const maxOrder = groupedItems
      .find((g) => g.sectionKey === createModalSection)
      ?.items.reduce((max, it) => Math.max(max, Number(it.order) || 0), 0) ?? 0;

    setIsCreating(true);
    try {
      await createContentItem({
        page: selectedPage,
        section: createModalSection,
        key,
        language,
        label,
        type: createForm.type,
        value: createForm.value,
        order: maxOrder + 1,
        isActive: createForm.isActive,
      });
      notify({ type: "success", title: "Item created" });
      setCreateModalSection("");
      await loadData();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "Try again.",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap gap-3 items-start sm:items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Content</h2>
            <p className="text-sm text-slate-500 mt-1">{t("dashboard.subtitle")}</p>
          </div>
          <button
            type="button"
            onClick={loadData}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            {t("content.reload")}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {languages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                i18n.changeLanguage(lang);
                localStorage.setItem("irqgyp_lang", lang);
                setLanguage(lang);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm border ${
                language === lang ? "bg-[#011337] text-white border-[#011337]" : "border-slate-300 hover:bg-slate-50"
              }`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500">{t("content.loading")}</p> : null}

      {!loading && normalizedItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
          {t("content.noItems")}
        </div>
      ) : null}

      <div className="space-y-5">
        {groupedItems.map((group) => (
          <section key={group.sectionKey} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            {(() => {
              const cardsMeta = getCardFieldMeta(group.items);
              const nonCardItems = cardsMeta
                ? group.items.filter((item) => !cardsMeta.cardItems.some((cardItem) => cardItem.id === item.id))
                : group.items;

              const saveCard = (card) => {
                actionWrap(
                  async () => {
                    await Promise.all(
                      card.fields.map(({ item }) => {
                        const nextValue =
                          Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                            ? cardDrafts[item.id]
                            : String(item.value ?? "");
                        return updateContentValue({ id: item.id, value: nextValue });
                      })
                    );
                  },
                  `Card ${card.index} updated`
                );
              };

              const addCardFromTemplate = async () => {
                if (!cardsMeta?.cards?.length) return;
                const firstCard = cardsMeta.cards[0];
                const nextIndex = Math.max(...cardsMeta.cards.map((c) => c.index)) + 1;
                const maxOrder = group.items.reduce(
                  (max, it) => Math.max(max, Number(it.order) || 0),
                  0
                );

                setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: true }));
                try {
                  await Promise.all(
                    firstCard.fields.map(({ suffix, item }, idx) =>
                      createContentItem({
                        page: selectedPage,
                        section: group.sectionKey,
                        key: `item${nextIndex}${suffix}`,
                        language,
                        label: `${toSectionTitle(group.sectionKey)} ${formatFieldTitle(suffix)} ${nextIndex}`,
                        type: item.type || "text",
                        value: "",
                        order: maxOrder + idx + 1,
                        isActive: Boolean(item.isActive),
                      })
                    )
                  );
                  notify({ type: "success", title: `Card ${nextIndex} created` });
                  await loadData();
                } catch (error) {
                  notify({
                    type: "error",
                    title: "Create card failed",
                    message: error?.response?.data?.message || error?.message || "Try again.",
                  });
                } finally {
                  setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: false }));
                }
              };

              const isMediaField = (suffix) => {
                const normalized = String(suffix || "").toLowerCase();
                return (
                  normalized.includes("icon") ||
                  normalized.includes("image") ||
                  normalized.includes("media") ||
                  normalized.includes("video")
                );
              };

              const deleteCardCompletely = async (card) => {
                const confirmed = window.confirm(`Delete Card ${card.index} completely? This cannot be undone.`);
                if (!confirmed) return;
                setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: true }));
                try {
                  await Promise.all(
                    card.fields.map(({ item }) => deleteContentItem({ id: item.id }))
                  );
                  notify({ type: "success", title: `Card ${card.index} deleted` });
                  await loadData();
                } catch (error) {
                  notify({
                    type: "error",
                    title: "Delete card failed",
                    message: error?.response?.data?.message || error?.message || "Try again.",
                  });
                } finally {
                  setBusyCardSections((prev) => ({ ...prev, [group.sectionKey]: false }));
                }
              };

              return (
                <>
            <div className="w-full flex items-center justify-between gap-3 pb-3 border-b border-slate-200">
              <button
                type="button"
                onClick={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    [group.sectionKey]: !prev[group.sectionKey],
                  }))
                }
                className="flex-1 min-w-0 flex items-center justify-between gap-3"
              >
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 text-left">
                  {group.sectionTitle}
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs sm:text-sm text-slate-500">
                    {group.items.length} item{group.items.length > 1 ? "s" : ""}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-[#011337] transition-transform ${
                      openSections[group.sectionKey] ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>
              <button
                type="button"
                onClick={() => openCreateModal(group.sectionKey)}
                className="text-xs sm:text-sm px-3 py-2 rounded-lg border border-[#FE7E3F] text-[#FE7E3F] hover:bg-[#FE7E3F]/10 whitespace-nowrap"
              >
                Add item
              </button>
            </div>

            <div className={`${openSections[group.sectionKey] ? "pt-4" : "hidden"}`}>
              {cardsMeta ? (
                <div className="mb-4 rounded-xl border border-[#011337]/15 bg-[#011337]/[0.03] p-3 sm:p-4">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <p className="text-sm font-semibold text-[#011337]">Cards editor</p>
                    <button
                      type="button"
                      onClick={addCardFromTemplate}
                      disabled={Boolean(busyCardSections[group.sectionKey])}
                      className="text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-[#011337]/30 text-[#011337] hover:bg-white disabled:opacity-60"
                    >
                      {busyCardSections[group.sectionKey] ? "Adding..." : "Add new card"}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {cardsMeta.cards.map((card) => (
                      <div key={card.index} className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-sm font-semibold text-[#011337] mb-2">Card {card.index}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {card.fields.map(({ suffix, item }) => (
                            <label key={item.id} className="text-xs text-slate-600">
                              {formatFieldTitle(suffix)}
                              <input
                                type="text"
                                value={
                                  Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                    ? cardDrafts[item.id]
                                    : String(item.value ?? "")
                                }
                                onChange={(e) =>
                                  setCardDrafts((prev) => ({ ...prev, [item.id]: e.target.value }))
                                }
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                              />
                              {isMediaField(suffix) &&
                              String(
                                Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                  ? cardDrafts[item.id]
                                  : item.value ?? ""
                              )
                                .trim()
                                .startsWith("/uploads/") ? (
                                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                                  {resolveMediaUrl(
                                    Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                      ? cardDrafts[item.id]
                                      : item.value
                                  ) ? (
                                    <img
                                      src={resolveMediaUrl(
                                        Object.prototype.hasOwnProperty.call(cardDrafts, item.id)
                                          ? cardDrafts[item.id]
                                          : item.value
                                      )}
                                      alt=""
                                      className="w-full h-24 object-contain bg-white rounded border border-slate-200"
                                    />
                                  ) : (
                                    <p className="text-[11px] text-slate-500">No media uploaded</p>
                                  )}
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    <input
                                      type="file"
                                      onChange={(e) =>
                                        setCardUploadFiles((prev) => ({
                                          ...prev,
                                          [item.id]: e.target.files?.[0] || null,
                                        }))
                                      }
                                      className="w-full text-xs file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-slate-200"
                                    />
                                    <button
                                      type="button"
                                      disabled={!cardUploadFiles[item.id] || Boolean(busyCardSections[group.sectionKey])}
                                      onClick={() =>
                                        actionWrap(
                                          () => uploadContentMedia({ id: item.id, file: cardUploadFiles[item.id] }),
                                          "Media uploaded"
                                        )
                                      }
                                      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-60"
                                    >
                                      Upload
                                    </button>
                                    <button
                                      type="button"
                                      disabled={Boolean(busyCardSections[group.sectionKey])}
                                      onClick={() =>
                                        actionWrap(() => deleteContentMedia({ id: item.id }), "Media removed")
                                      }
                                      className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                            </label>
                          ))}
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => deleteCardCompletely(card)}
                            disabled={Boolean(busyCardSections[group.sectionKey])}
                            className="text-sm px-3 py-2 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                          >
                            Delete card
                          </button>
                          <button
                            type="button"
                            onClick={() => saveCard(card)}
                            disabled={Boolean(busyCardSections[group.sectionKey])}
                            className="text-sm px-3 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90"
                          >
                            Save card
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                <table className="min-w-full bg-white table-fixed">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="w-[24%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Field</th>
                      <th className="w-[41%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Value</th>
                      <th className="w-[25%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Media</th>
                      <th className="w-[10%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nonCardItems.map((item) => (
                      <EditableContentRow
                        key={`${item.path}-${item.id || item.key}`}
                        item={item}
                        t={t}
                        onSaveValue={(it, value) =>
                          requireId(it) &&
                          actionWrap(() => updateContentValue({ id: it.id, value }), "Content updated successfully")
                        }
                        onSaveOrder={(it, order) =>
                          requireId(it) &&
                          actionWrap(() => updateContentOrder({ id: it.id, order }), "Order updated")
                        }
                        onToggleActive={(it, isActive) =>
                          requireId(it) &&
                          actionWrap(() => updateContentActive({ id: it.id, isActive }), "Status updated")
                        }
                        onUploadMedia={(it, file) =>
                          requireId(it) &&
                          actionWrap(() => uploadContentMedia({ id: it.id, file }), "Media uploaded")
                        }
                        onRemoveMedia={(it) =>
                          requireId(it) && actionWrap(() => deleteContentMedia({ id: it.id }), "Media removed")
                        }
                        onDeleteItem={(it) =>
                          requireId(it) &&
                          window.confirm(`Delete "${it.label || it.key}"?`) &&
                          actionWrap(() => deleteContentItem({ id: it.id }), "Item deleted")
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
                </>
              );
            })()}
          </section>
        ))}
      </div>

      {createModalSection ? (
        <div className="fixed inset-0 z-[12000] bg-slate-900/45 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="px-4 py-3 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-[#011337]">Add New Item</h3>
              <p className="text-sm text-slate-500 mt-1">
                Section: {toSectionTitle(createModalSection)} | Language: {language.toUpperCase()}
              </p>
            </div>

            <div className="p-4 space-y-3">
              <label className="block text-sm text-slate-700">
                Label
                <input
                  type="text"
                  value={createForm.label}
                  onChange={(e) => {
                    const nextLabel = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      label: nextLabel,
                      key: prev.key || toKeyFromLabel(nextLabel),
                    }));
                  }}
                  placeholder="Example: Hero Title"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Key
                <input
                  type="text"
                  value={createForm.key}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, key: e.target.value }))}
                  placeholder="Auto generated from label"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Type
                <select
                  value={createForm.type}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, type: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="text">Text</option>
                  <option value="textarea">Textarea</option>
                  <option value="richtext">Rich text</option>
                  <option value="link">Link</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </label>

              <label className="block text-sm text-slate-700">
                Initial Value
                <textarea
                  value={createForm.value}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, value: e.target.value }))}
                  placeholder="Enter content..."
                  className="mt-1 w-full min-h-[110px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>

              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={createForm.isActive}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCreateModal}
                disabled={isCreating}
                className="text-sm px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateSubmit}
                disabled={isCreating}
                className="text-sm px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 disabled:opacity-60"
              >
                {isCreating ? "Creating..." : "Create Item"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ContentManager;
